import type { ReactNode } from 'react'
import { useMarketingSettings, type MarketingSettings } from '../hooks/useMarketingSettings.js'
import { Card, Empty, Field, Notice, PageHead, RowsSkeleton, SettingRow, Switch } from '../components/ui.js'
import { BundleList } from '../components/BundleEditor.js'
import { ClockIcon, CartIcon, StoreIcon, PaletteIcon, HeadsetIcon } from '../components/icons.js'

/**
 * The storefront campaigns.
 *
 * Deliberately its own screen rather than a panel on the Buy Now page. The
 * countdown and the recommendation list are drawn by the same embed script as
 * the button, which is why they were once filed under it — but a store can run
 * a campaign with the button switched off, and most do: the script is on the
 * product page either way, and rendering the campaign is half of what it is
 * there for.
 */
function MarketingFrame({
  title,
  subtitle,
  crumb,
  children,
}: {
  title: string
  subtitle: string
  crumb: string
  children: (settings: MarketingSettings, set: ReturnType<typeof useMarketingSettings>['set']) => ReactNode
}): ReactNode {
  const { shop, settings, isPending, error, dirty, saved, saving, saveError, set, save } = useMarketingSettings()

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Campaigns belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  return (
    <>
      <PageHead
        title={title}
        subtitle={subtitle}
        crumbs={[{ label: 'Other Product' }, { label: crumb }]}
        actions={
          <button type="button" className="btn btn--primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        }
      />

      {saved && (
        <Notice kind="ok" title="Saved">
          Your storefront picks this up on the next page load.
        </Notice>
      )}

      {saveError && (
        <Notice kind="error" title="Could not save">
          {(saveError as Error).message}
        </Notice>
      )}

      {dirty && !saving && (
        <Notice kind="warn" title="Unsaved changes">
          Nothing changes for shoppers until you press Save.
        </Notice>
      )}

      {error && (
        <Card>
          <Empty error title="Could not load your campaigns">
            {(error as Error).message}
          </Empty>
        </Card>
      )}

      {isPending && (
        <Card>
          <RowsSkeleton rows={6} />
        </Card>
      )}

      {settings && children(settings, set)}
    </>
  )
}

// ── Countdown ───────────────────────────────────────────────────────────────

const WEEKDAYS = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '7', label: 'Sun' },
]

export function CountdownPage(): ReactNode {
  return (
    <MarketingFrame
      title="Countdown timer"
      subtitle="A sale timer on your product pages. Independent of the Buy Now button."
      crumb="Countdown timer"
    >
      {(s, set) => (
        <div className="grid grid--main-wide">
          <div className="stack">
            <Card title="Campaign" subtitle="The server owns the end instant; the browser only renders it.">
              <SettingRow
                title="Show the countdown"
                description="Appears on product pages, under the buy buttons."
              >
                <Switch
                  label="Show the countdown"
                  checked={s.countdownEnabled}
                  onChange={(next) => set('countdownEnabled', next)}
                />
              </SettingRow>

              <Field label="Heading">
                <input
                  className="input"
                  value={s.countdownTitle}
                  maxLength={60}
                  onChange={(event) => set('countdownTitle', event.target.value)}
                />
              </Field>

              <Field label="Sub-heading">
                <input
                  className="input"
                  value={s.countdownSubtitle}
                  maxLength={60}
                  onChange={(event) => set('countdownSubtitle', event.target.value)}
                />
              </Field>

              <Field
                label="Where it appears"
                hint={
                  s.countdownDisplayOn === 'product'
                    ? 'Only where a shopper is deciding on one product.'
                    : 'Anywhere the storefront shows a buy button — including your home page and collection grids.'
                }
              >
                <select
                  className="input"
                  value={s.countdownDisplayOn}
                  onChange={(event) =>
                    set('countdownDisplayOn', event.target.value as MarketingSettings['countdownDisplayOn'])
                  }
                >
                  <option value="product">Product pages only</option>
                  <option value="all">Every page with a buy button</option>
                </select>
              </Field>

              {/*
                Worth saying out loud: a home page carrying a featured product,
                or a collection grid with quick-add tiles, has a buy form on it —
                so "every page" really does mean the shop front too, which is not
                what most merchants picture.
              */}
              {s.countdownDisplayOn === 'all' && (
                <Notice kind="info" title="This includes your home page">
                  A featured product or a quick-add tile counts as a buy button, so the timer will show on your
                  shop front and collection pages as well.
                </Notice>
              )}
            </Card>

            <Card title="When it ends">
              <Field
                label="Timer type"
                hint={
                  s.countdownType === 'duration'
                    ? 'Each shopper gets their own window, starting the first time they see it.'
                    : s.countdownType === 'date'
                      ? 'One fixed moment, the same for everybody.'
                      : 'A window that reopens on the days you pick.'
                }
              >
                <select
                  className="input"
                  value={s.countdownType}
                  onChange={(event) => set('countdownType', event.target.value as MarketingSettings['countdownType'])}
                >
                  <option value="duration">Per-shopper countdown</option>
                  <option value="date">Fixed end date</option>
                  <option value="daily">Daily window</option>
                </select>
              </Field>

              {s.countdownType === 'duration' && (
                <Field label="Length" hint="How long each shopper's window lasts.">
                  <select
                    className="input"
                    value={String(s.countdownDuration)}
                    onChange={(event) => set('countdownDuration', Number(event.target.value))}
                  >
                    <option value="600">10 minutes</option>
                    <option value="1200">20 minutes</option>
                    <option value="1800">30 minutes</option>
                    <option value="3600">1 hour</option>
                    <option value="10800">3 hours</option>
                    <option value="23700">6 hours 35 minutes</option>
                    <option value="86400">24 hours</option>
                  </select>
                </Field>
              )}

              {s.countdownType === 'date' && (
                <Field label="Ends at" hint="Your store's timezone.">
                  <input
                    type="datetime-local"
                    className="input"
                    value={s.countdownEndsAt}
                    onChange={(event) => set('countdownEndsAt', event.target.value)}
                  />
                </Field>
              )}

              {s.countdownType === 'daily' && (
                <>
                  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="Starts">
                      <input
                        type="time"
                        className="input"
                        value={s.countdownDailyStart}
                        onChange={(event) => set('countdownDailyStart', event.target.value)}
                      />
                    </Field>
                    <Field label="Ends">
                      <input
                        type="time"
                        className="input"
                        value={s.countdownDailyEnd}
                        onChange={(event) => set('countdownDailyEnd', event.target.value)}
                      />
                    </Field>
                  </div>

                  <Field label="Days" hint="At least one.">
                    <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                      {WEEKDAYS.map((day) => {
                        const chosen = s.countdownDailyDays.split(',').filter(Boolean)
                        const on = chosen.includes(day.value)

                        return (
                          <button
                            key={day.value}
                            type="button"
                            className={`btn${on ? ' btn--primary' : ''}`}
                            onClick={() => {
                              const next = on
                                ? chosen.filter((value) => value !== day.value)
                                : [...chosen, day.value].sort()

                              // Never save an empty list — the API rejects it,
                              // and a campaign on no days is a campaign that is
                              // simply off, which the switch above already says.
                              if (next.length > 0) set('countdownDailyDays', next.join(','))
                            }}
                          >
                            {day.label}
                          </button>
                        )
                      })}
                    </div>
                  </Field>
                </>
              )}

              <Field label="When it runs out">
                <select
                  className="input"
                  value={s.countdownOnEnd}
                  onChange={(event) => set('countdownOnEnd', event.target.value as MarketingSettings['countdownOnEnd'])}
                >
                  <option value="message">Show a message</option>
                  <option value="repeat">Start again</option>
                  <option value="hide">Hide the banner</option>
                  <option value="nothing">Leave it at zero</option>
                </select>
              </Field>

              {s.countdownOnEnd === 'message' && (
                <Field label="Message">
                  <input
                    className="input"
                    value={s.countdownEndedText}
                    maxLength={60}
                    onChange={(event) => set('countdownEndedText', event.target.value)}
                  />
                </Field>
              )}
            </Card>
          </div>

          <div className="stack">
            <Card title="Appearance" subtitle="How the banner looks on your storefront.">
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Background">
                  <input
                    type="color"
                    className="input"
                    value={s.countdownBg}
                    onChange={(event) => set('countdownBg', event.target.value)}
                  />
                </Field>
                <Field label="Text">
                  <input
                    type="color"
                    className="input"
                    value={s.countdownText}
                    onChange={(event) => set('countdownText', event.target.value)}
                  />
                </Field>
              </div>

              {/*
                A white banner on a white product page is the one combination
                that reads as "the countdown is not working" while the countdown
                is working perfectly.
              */}
              {s.countdownBg.toLowerCase() === '#ffffff' && (
                <Notice kind="warn" title="This banner may be invisible">
                  A white background on a white product page shows as plain text. Pick a colour that stands out.
                </Notice>
              )}

              <div
                style={{
                  background: s.countdownBg,
                  color: s.countdownText,
                  padding: '12px 14px',
                  borderRadius: 12,
                  textAlign: 'center',
                  border: '1px solid var(--line)',
                }}
              >
                <strong style={{ display: 'block' }}>{s.countdownTitle || 'Hurry up!'}</strong>
                <small>{s.countdownSubtitle || 'Sale ends in:'}</small>
                <div style={{ fontVariantNumeric: 'tabular-nums', fontSize: '1.3rem', fontWeight: 650 }}>
                  02 : 14 : 51
                </div>
              </div>
            </Card>

            <Card title="Call to action" subtitle="Optional. A link under the timer.">
              <Field label="Label">
                <input
                  className="input"
                  value={s.countdownCtaLabel}
                  maxLength={40}
                  placeholder="Shop the sale"
                  onChange={(event) => set('countdownCtaLabel', event.target.value)}
                />
              </Field>
              <Field label="Link">
                <input
                  className="input"
                  value={s.countdownCtaUrl}
                  maxLength={500}
                  placeholder="/collections/sale"
                  onChange={(event) => set('countdownCtaUrl', event.target.value)}
                />
              </Field>
            </Card>

            <Card title="Bundle discount" subtitle="Whether an expired timer also withdraws the offer.">
              <SettingRow
                title="Tie the offer to the timer"
                description="Checked again on the server before every charge — the timer on screen decides nothing."
              >
                <Switch
                  label="Tie the offer to the timer"
                  checked={s.countdownControlsOffer}
                  onChange={(next) => set('countdownControlsOffer', next)}
                />
              </SettingRow>

              {s.countdownControlsOffer && (
                <Field label="Once it has expired">
                  <select
                    className="input"
                    value={s.countdownExpiredAction}
                    onChange={(event) =>
                      set('countdownExpiredAction', event.target.value as MarketingSettings['countdownExpiredAction'])
                    }
                  >
                    <option value="hide">Hide the offer</option>
                    <option value="no_discount">Show it without the discount</option>
                    <option value="message">Show it with a message</option>
                  </select>
                </Field>
              )}
            </Card>
          </div>
        </div>
      )}
    </MarketingFrame>
  )
}

// ── Frequently bought together ──────────────────────────────────────────────

export function BundlesPage(): ReactNode {
  return (
    <MarketingFrame
      title="Frequently bought together"
      subtitle="Recommend products alongside the one a shopper is looking at."
      crumb="Frequently bought together"
    >
      {(s, set) => (
        <div className="grid grid--main-wide">
          <div className="stack">
            <Card title="Recommendations">
              <SettingRow
                title="Show recommendations"
                description="Priced live from your catalogue when a shopper opens the page."
              >
                <Switch
                  label="Show recommendations"
                  checked={s.fbtEnabled}
                  onChange={(next) => set('fbtEnabled', next)}
                />
              </SettingRow>

              <Field label="Heading">
                <input
                  className="input"
                  value={s.fbtHeading}
                  maxLength={60}
                  onChange={(event) => set('fbtHeading', event.target.value)}
                />
              </Field>

              <Field
                label="Where it appears"
                hint="The countdown has no such choice — it only ever runs on product pages."
              >
                <select
                  className="input"
                  value={s.fbtDisplayOn}
                  onChange={(event) => set('fbtDisplayOn', event.target.value as MarketingSettings['fbtDisplayOn'])}
                >
                  <option value="checkout">In the checkout</option>
                  <option value="product">On product pages</option>
                  <option value="both">Both</option>
                </select>
              </Field>
            </Card>

            {/*
              Under the switch, not on a screen of its own.

              These two were split once and it was the whole bug: the toggle
              said the feature was on, there was nowhere to say what it should
              recommend, and the widget correctly rendered nothing forever.
            */}
            <BundleList />
          </div>

          <div className="stack">
            <Card title="Need help? Chat" subtitle="A WhatsApp link under the recommendations.">
              <SettingRow
                title="Show the help link"
                description="Opens WhatsApp on your support number. It does nothing while that number is blank."
              >
                <Switch
                  label="Show the help link"
                  checked={s.fbtHelpEnabled}
                  onChange={(next) => set('fbtHelpEnabled', next)}
                />
              </SettingRow>

              <p className="muted" style={{ fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <HeadsetIcon size={15} />
                Set the number under Checkout → Checkout design → Brand.
              </p>
            </Card>

            <Card title="What a shopper sees">
              <div className="row" style={{ gap: 10, marginBottom: 10 }}>
                <ClockIcon size={16} />
                <span className="muted" style={{ fontSize: '.85rem' }}>
                  The countdown, if it is running.
                </span>
              </div>
              <div className="row" style={{ gap: 10, marginBottom: 10 }}>
                <CartIcon size={16} />
                <span className="muted" style={{ fontSize: '.85rem' }}>
                  {s.fbtHeading || 'Frequently bought together'}
                </span>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <PaletteIcon size={16} />
                <span className="muted" style={{ fontSize: '.85rem' }}>
                  Both are drawn by the storefront embed, under your buy buttons.
                </span>
              </div>
            </Card>
          </div>
        </div>
      )}
    </MarketingFrame>
  )
}
